import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import { useToggle } from '../../hooks';

type PreferenceContextType = {
  showNavBar: boolean;
  showSideBar: boolean;
  website: boolean;
  version: string;
  v2Scheduler: boolean;
  isSidebarMinimized: boolean;
  toggleSidebarMinimized: () => void;
  setSidebarMinimized: (value: boolean) => void;
};

const PreferenceContext = createContext<PreferenceContextType | undefined>(
  undefined
);

type PreferenceProviderProps = PropsWithChildren;

const PreferenceProvider = ({ children }: PreferenceProviderProps) => {
  const getInitialValue = useCallback((key: string) => {
    const value = localStorage.getItem(key);
    return value === null || value === 'true';
  }, []);

  const {
    isOpen: isSidebarMinimized,
    toggle: toggleSidebarMinimized,
    setIsOpen: setSidebarMinimized,
  } = useToggle(false, {
    withStorage: true,
    storageKey: 'isSidebarMinimized',
  });

  const context = useMemo(() => {
    const showNavBar = getInitialValue('showNavBar');
    const showSideBar = getInitialValue('showSideBar');
    const website = showNavBar;
    const v2Scheduler = showNavBar;
    return {
      showNavBar,
      showSideBar,
      website,
      version: website ? 'v2' : '',
      v2Scheduler,
      isSidebarMinimized,
      toggleSidebarMinimized,
      setSidebarMinimized,
    };
  }, [
    getInitialValue,
    isSidebarMinimized,
    toggleSidebarMinimized,
    setSidebarMinimized,
  ]);

  return (
    <PreferenceContext.Provider value={context}>
      {children}
    </PreferenceContext.Provider>
  );
};

const usePreferenceContext = () => {
  const context = useContext(PreferenceContext);
  if (!context)
    throw new Error(
      'usePreferenceContext must be used inside PreferenceProvider!'
    );
  return context;
};

export { PreferenceProvider, usePreferenceContext };
